import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ClockIcon, CheckIcon, ShieldIcon, DownloadIcon } from '../components/Icons';
import { HowItWorksButton, TIMESHEET_HELP } from '../components/HowItWorks';

// Cycle order on click
const CODES = ['P', 'A', 'O', 'SL', 'AL', 'UL', 'PH', 'NE', 'LWD'];
const CODE_STYLE = {
  P:  { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Present' },
  A:  { bg: 'bg-red-100', text: 'text-red-600', label: 'Absent' },
  O:  { bg: 'bg-slate-200', text: 'text-slate-600', label: 'Day Off' },
  SL: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Sick (Sehaty)' },
  AL: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Annual Leave (basic pay)' },
  UL: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Unpaid Leave' },
  PH: { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Public Holiday' },
  NE: { bg: 'bg-slate-300', text: 'text-slate-500', label: 'Not Employed' },
  LWD: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Last Working Day' },
};

const PDF_COLORS = {
  P:   [[236, 253, 245], [5, 150, 105]],
  A:   [[254, 226, 226], [220, 38, 38]],
  O:   [[226, 232, 240], [71, 85, 105]],
  SL:  [[254, 243, 199], [180, 83, 9]],
  AL:  [[204, 251, 241], [15, 118, 110]],
  UL:  [[255, 237, 213], [194, 65, 12]],
  PH:  [[219, 234, 254], [37, 99, 235]],
  NE:  [[203, 213, 225], [71, 85, 105]],
  LWD: [[243, 232, 255], [126, 34, 206]],
};

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = -1; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    opts.push({ val, label });
  }
  return opts;
}

export default function Timesheets() {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const readOnly = user?.role === 'viewer'; // read-only accounts can view but never edit
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(isClient ? (user?.project || '') : '');
  const [locked, setLocked] = useState(false);
  const [month, setMonth] = useState(monthOptions()[1].val);
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);

  const [y, m] = month.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const dayName = d => new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'narrow' });
  const isFriday = d => new Date(y, m - 1, d).getDay() === 5;

  useEffect(() => {
    if (isClient) return; // clients are pinned to their own project
    api.get('/projects').then(r => {
      setProjects(r.data);
      if (r.data.length && !project) setProject(r.data[0]);
    });
    // eslint-disable-next-line
  }, []);

  const load = useCallback(() => {
    if (!project || !month) return;
    setLoading(true);
    api.get('/timesheets', { params: { project, month } })
      .then(r => { setEmployees(r.data.employees); setEntries(r.data.entries); setLocked(!!r.data.locked); setDirty(false); setSavedAt(null); })
      .finally(() => setLoading(false));
  }, [project, month]);

  useEffect(() => { load(); }, [load]);

  // Day is before the joining date → always NE (payroll derives the same on the server).
  // Parsed as numbers, not new Date(), to avoid timezone off-by-one.
  function isPreJoin(emp, d) {
    const jm = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(emp.joiningDate || ''));
    if (!jm) return false;
    const jy = +jm[1], jmo = +jm[2], jd = +jm[3];
    if (jy < y || (jy === y && jmo < m)) return false;
    if (jy === y && jmo === m) return d < jd;
    return true; // joins after this month
  }

  function effectiveCode(emp, d) {
    if (isPreJoin(emp, d)) return 'NE';
    const stored = entries[emp.id]?.days?.[d];
    if (stored) return stored;
    return 'P';
  }

  function cycle(emp, d) {
    if (locked || readOnly) return;
    if (isPreJoin(emp, d)) return; // not employed yet — not editable
    const cur = effectiveCode(emp, d);
    const next = CODES[(CODES.indexOf(cur) + 1) % CODES.length];
    setEntries(prev => {
      const empEntry = { ...(prev[emp.id] || {}) };
      const days2 = { ...(empEntry.days || {}) };
      if (next === 'P') delete days2[d];
      else days2[d] = next;
      empEntry.days = days2;
      return { ...prev, [emp.id]: empEntry };
    });
    setDirty(true);
  }

  function setOT(empId, val) {
    if (locked || readOnly) return;
    setEntries(prev => ({ ...prev, [empId]: { ...(prev[empId] || {}), otAmount: val } }));
    setDirty(true);
  }

  async function toggleLock() {
    const next = !locked;
    if (next && dirty) await save();
    await api.post('/timesheets/lock', { project, month, locked: next });
    setLocked(next);
  }

  async function save() {
    setSaving(true);
    try {
      await api.put('/timesheets', { project, month, entries });
      setDirty(false);
      setSavedAt(new Date());
    } finally { setSaving(false); }
  }

  function summary(emp) {
    const counts = { A: 0, O: 0, SL: 0, UL: 0, PH: 0, NE: 0 };
    days.forEach(d => { const c = effectiveCode(emp, d); if (counts[c] !== undefined) counts[c]++; });
    const present = daysInMonth - Object.values(counts).reduce((s, n) => s + n, 0);
    return { present, ...counts };
  }

  // Full per-code count for the PDF summary (e.g. "17P 13NE")
  function codeCounts(emp) {
    const c = {};
    days.forEach(d => { const k = effectiveCode(emp, d); c[k] = (c[k] || 0) + 1; });
    return CODES.filter(k => c[k]).map(k => `${c[k]}${k}`).join(' ');
  }

  function exportPDF() {
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const M = 24;
    const monthName = new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const status = dirty ? 'UNSAVED CHANGES' : locked ? 'LOCKED' : 'DRAFT — not locked';

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(15, 27, 51);
    doc.text(`Delta HR — Timesheet ${monthName} — ${project}`, M, 34);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(100, 116, 139);
    doc.text(`${status} · ${employees.length} employees · generated ${new Date().toLocaleString('en-GB')}`, M, 47);
    doc.setFontSize(7.5);
    doc.text(CODES.map(k => `${k} = ${CODE_STYLE[k].label}`).join('   ·   '), M, 59);

    const dayCol = 17.5;
    const columnStyles = { 0: { cellWidth: 140, halign: 'left' } };
    days.forEach((_, i) => { columnStyles[i + 1] = { cellWidth: dayCol, halign: 'center' }; });
    columnStyles[days.length + 1] = { cellWidth: 36, halign: 'right' };
    columnStyles[days.length + 2] = { cellWidth: 'auto', halign: 'left', fontStyle: 'bold' };

    autoTable(doc, {
      startY: 68,
      margin: { left: M, right: M },
      theme: 'grid',
      head: [[
        'Employee',
        ...days.map(d => `${d}\n${dayName(d)}`),
        'OT (SAR)',
        'Summary',
      ]],
      body: employees.map(emp => [
        `${emp.name}\n${emp.designation || ''}`,
        ...days.map(d => effectiveCode(emp, d)),
        String(Number(entries[emp.id]?.otAmount) || 0),
        codeCounts(emp),
      ]),
      styles: { fontSize: 6, cellPadding: 2, lineColor: [226, 232, 240], lineWidth: 0.4, textColor: [30, 41, 59], valign: 'middle' },
      headStyles: { fillColor: [15, 27, 51], textColor: 255, fontSize: 6, fontStyle: 'bold', halign: 'center' },
      columnStyles,
      didParseCell: (data) => {
        const col = data.column.index;
        if (col >= 1 && col <= days.length) {
          const d = days[col - 1];
          if (data.section === 'head' && isFriday(d)) data.cell.styles.fillColor = [51, 65, 85];
          if (data.section === 'body') {
            const c = PDF_COLORS[data.cell.raw];
            if (c) { data.cell.styles.fillColor = c[0]; data.cell.styles.textColor = c[1]; }
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = data.cell.raw === 'LWD' ? 4.5 : 5.5;
          }
        }
        if (data.section === 'body' && col === 0) data.cell.styles.fontSize = 6.5;
      },
    });

    // Signature lines (client + Delta HR)
    const endY = Math.min(doc.lastAutoTable.finalY + 40, doc.internal.pageSize.getHeight() - 30);
    doc.setDrawColor(148, 163, 184); doc.setFontSize(8); doc.setTextColor(100, 116, 139);
    doc.line(M, endY, M + 180, endY);          doc.text('Client signature & stamp', M, endY + 11);
    doc.line(M + 260, endY, M + 440, endY);    doc.text('Delta HR', M + 260, endY + 11);

    doc.save(`timesheet-${month}-${String(project).replace(/[^a-z0-9]+/gi, '_')}.pdf`);
  }

  const selCls = "px-3.5 py-2.5 border border-slate-200 rounded-xl text-[13px] bg-white text-slate-600 outline-none focus:border-[#fb866d] cursor-pointer transition-colors";

  return (
    <div>
      <div className="hidden lg:flex bg-white border-b border-slate-100 px-8 py-4 items-center justify-between sticky top-0 z-40">
        <h1 className="text-lg font-bold text-slate-800">Timesheets</h1>
        <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-semibold text-slate-600">
          {isClient ? project : 'Admin'}
        </span>
      </div>

      <div className="p-4 lg:p-8 stagger">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0f1b33] flex items-center justify-center">
              <ClockIcon size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-[17px] font-bold text-slate-800">Monthly Attendance</h2>
              <p className="text-[13px] text-slate-400">All cells default to Present — click a cell to mark exceptions.</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            {savedAt && !dirty && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                <CheckIcon size={13} /> Saved
              </span>
            )}
            {!isClient && !readOnly && (
              <button onClick={toggleLock}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold border transition-all ${
                  locked ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:border-slate-400'
                }`}>
                <ShieldIcon size={14} /> {locked ? 'Locked — Unlock' : 'Lock Month'}
              </button>
            )}
            <HowItWorksButton title="How Timesheets Work" sections={TIMESHEET_HELP} />
            <button onClick={exportPDF} disabled={loading || employees.length === 0}
              title={dirty ? 'Save first — PDF will be marked UNSAVED CHANGES' : 'Download timesheet as PDF'}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold border border-slate-200 text-slate-600 hover:border-slate-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              <DownloadIcon size={14} /> PDF
            </button>
            {!readOnly && (
              <button onClick={save} disabled={saving || !dirty || locked}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-[13px] font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? 'Saving...' : dirty ? 'Save Changes' : 'Saved'}
              </button>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex items-center gap-3 flex-wrap" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          {isClient ? (
            <span className="px-4 py-2.5 rounded-xl bg-[#0f1b33] text-white text-[13px] font-semibold">{project}</span>
          ) : (
            <select className={selCls} value={project} onChange={e => setProject(e.target.value)}>
              {projects.map(p => <option key={p}>{p}</option>)}
            </select>
          )}
          <select className={selCls} value={month} onChange={e => setMonth(e.target.value)}>
            {monthOptions().map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
          </select>
          {/* Legend */}
          <div className="flex items-center gap-2 flex-wrap ml-auto">
            {Object.entries(CODE_STYLE).map(([code, s]) => (
              <span key={code} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10.5px] font-bold ${s.bg} ${s.text}`}>
                {code} <span className="font-medium opacity-70">{s.label}</span>
              </span>
            ))}
          </div>
        </div>

        {locked && (
          <div className="flex items-center gap-2.5 px-4 py-3 mb-4 rounded-xl bg-emerald-50 border border-emerald-200 text-[13px] text-emerald-800">
            <ShieldIcon size={16} className="flex-shrink-0" />
            This month is locked{isClient ? ' by Delta HR' : ''} — attendance is final and feeding payroll.{isClient ? ' Contact Delta HR for corrections.' : ''}
          </div>
        )}

        {/* Grid */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2.5 text-slate-400 text-sm">
              <div className="w-4 h-4 border-2 border-slate-200 border-t-[#fb866d] rounded-full animate-spin" />
              Loading...
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-[15px] font-semibold text-slate-500">No active employees on this project</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="border-collapse" style={{ minWidth: 900 }}>
                <thead>
                  <tr className="bg-slate-50/70">
                    <th className="sticky left-0 bg-slate-50 z-10 px-4 py-3 text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-r border-slate-100 min-w-[180px]">
                      Employee
                    </th>
                    {days.map(d => (
                      <th key={d} className={`px-0.5 py-2 text-center text-[10px] font-bold border-b border-slate-100 min-w-[30px] ${isFriday(d) ? 'bg-slate-100 text-slate-500' : 'text-slate-400'}`}>
                        <div>{d}</div>
                        <div className="font-medium opacity-60">{dayName(d)}</div>
                      </th>
                    ))}
                    <th className="px-3 py-3 text-center text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-l border-slate-100 min-w-[70px]">OT (SAR)</th>
                    <th className="px-3 py-3 text-center text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 min-w-[90px]">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map(emp => {
                    const s = summary(emp);
                    return (
                      <tr key={emp.id} className="border-b border-slate-50 last:border-0">
                        <td className="sticky left-0 bg-white z-10 px-4 py-2 border-r border-slate-100">
                          <div className="text-[13px] font-semibold text-slate-800 whitespace-nowrap">{emp.name}</div>
                          {emp.transferredTo && (
                            <div className="text-[10px] font-semibold text-purple-600 mt-0.5">&rarr; moved to {emp.transferredTo}</div>
                          )}
                          <div className="text-[10.5px] text-slate-400">{emp.designation || '—'}</div>
                        </td>
                        {days.map(d => {
                          const code = effectiveCode(emp, d);
                          const st = CODE_STYLE[code];
                          return (
                            <td key={d} className="p-0.5 text-center">
                              <button onClick={() => cycle(emp, d)}
                                title={`${emp.name} — ${d}/${m}: ${st.label} (click to change)`}
                                className={`w-[26px] h-[26px] rounded-md text-[10px] font-bold transition-all hover:ring-2 hover:ring-[#fb866d]/50 ${st.bg} ${st.text} ${code === 'P' ? 'opacity-45 hover:opacity-100' : ''}`}>
                                {code === 'P' ? 'P' : code}
                              </button>
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 border-l border-slate-100">
                          <input type="number" min="0" value={entries[emp.id]?.otAmount ?? ''}
                            disabled={locked || readOnly}
                            onChange={e => setOT(emp.id, e.target.value)}
                            placeholder="0"
                            title="Overtime amount in SAR (as calculated by the client) — added to salary"
                            className="w-16 px-1.5 py-1 border border-slate-200 rounded-lg text-[12px] text-center outline-none focus:border-[#fb866d] disabled:bg-slate-100" />
                        </td>
                        <td className="px-3 py-2 text-center whitespace-nowrap">
                          <span className="text-[11px] font-semibold text-emerald-600">{s.present}P</span>
                          {s.A > 0 && <span className="text-[11px] font-semibold text-red-500 ml-1.5">{s.A}A</span>}
                          {s.SL > 0 && <span className="text-[11px] font-semibold text-amber-600 ml-1.5">{s.SL}SL</span>}
                          {s.UL > 0 && <span className="text-[11px] font-semibold text-orange-600 ml-1.5">{s.UL}UL</span>}
                          {s.O > 0 && <span className="text-[11px] font-semibold text-slate-500 ml-1.5">{s.O}O</span>}
                          {s.NE > 0 && <span className="text-[11px] font-semibold text-slate-400 ml-1.5">{s.NE}NE</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-400 mt-3">
          {isClient
            ? 'Click a cell to change a day\'s status. All days start as Present — mark absences as they happen, enter the overtime amount (SAR) in the OT box, then Save. Delta HR reviews and locks the month for payroll.'
            : 'Fridays are shaded. Days before an employee\'s joining date are auto-marked NE. OT hrs = total overtime for the month — paid at each client\'s OT multiplier (set in Clients page). Mark LWD (Last Working Day) when an employee transfers to another client mid-month — attendance from both clients counts in payroll. Lock the month after review to freeze it for payroll.'}
        </p>
      </div>
    </div>
  );
}